import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, X } from 'lucide-react';
import PharmacyService from '../Pharmacy/PharmacyService';
import './PharmacyAppointments.css';

const PharmacyAppointments = () => {
  const { pharmacyId } = useParams();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMedicines, setSelectedMedicines] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Changed from 10 to 5

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        setLoading(true);
        const response = await PharmacyService.getPharmacyAppointments(pharmacyId);
        if (response.status === 1 && Array.isArray(response.data)) {
          setAppointments(response.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        // setError(`Failed to load appointments: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [pharmacyId]);

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    const date = new Date(dateTime);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours));
    date.setMinutes(parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatCurrency = (amount) => {
    return amount !== null ? `₹${amount.toFixed(2)}` : 'N/A';
  };

  const getAppointmentType = (type) => {
    return type === 1 ? 'In-Person' : 'Online';
  };

  const parseMedicineDetails = (medicineDetails) => {
    try {
      const parsed = JSON.parse(medicineDetails);
      return parsed.map(med => ({
        name: med.name || med.label,
        counts: med.counts,
        days: med.days,
        price: med.price,
        totalMedicine: med.totalmedicine,
        foodType: med.foodtype,
      }));
    } catch (error) {
      return [];
    }
  };

  const getFoodType = (foodType) => {
    return foodType === 0 ? 'Before Food' : 'After Food';
  };

  const openMedicineModal = (medicineDetails) => {
    const medicines = parseMedicineDetails(medicineDetails);
    setSelectedMedicines(medicines);
    setIsModalOpen(true);
  };

  const closeMedicineModal = () => {
    setIsModalOpen(false);
    setSelectedMedicines(null);
  };

  // Pagination logic
  const totalPages = Math.ceil(appointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAppointments = appointments.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="doctor-appointments">
      <div className="appointments-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} /> Back
        </button>
        <h2>Pharmacy Appointments</h2>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading">Loading appointments...</div>
      ) : (
        <div className="appointments-content">
          {appointments.length === 0 ? (
            <div className="no-appointments">
              <p>No appointments found.</p>
            </div>
          ) : (
            <>
              <div className="appointments-summary">
                <p>Showing {startIndex + 1} to {Math.min(endIndex, appointments.length)} of {appointments.length} appointments</p>
              </div>
              
              <div className="appointments-table-container">
                <table className="appointments-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Patient Name</th>
                      <th>Age</th>
                      <th>Gender</th>
                      <th>Phone Number</th>
                      <th>Prescription Date</th>
                      <th>Appointment Date</th>
                      {/* <th>Payment Status</th> */}
                      <th>Medicines</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentAppointments.map((appointment) => (
                      <tr key={appointment.id}>
                        <td className="appointment-id">{`PHARM-${appointment.id}`}</td>                        <td className="patient-name">{appointment.patient_name || 'N/A'}</td>
                        <td className="patient-age">{appointment.age || 'N/A'}</td>
                        <td className="patient-gender">{appointment.gender || 'N/A'}</td>
                        <td className="patient-phone">{appointment.phonenumber || 'N/A'}</td>
                        <td className="prescription-date">
                          {formatDateTime(appointment.prescription_date)}
                        </td>
                        <td className="appointment-date">
                          {formatDateTime(appointment.appointment_date)}
                        </td>
                        {/* <td>
                          <span className={`payment-badge ${appointment.payment_status === 'Paid' ? 'paid' : 'unpaid'}`}>
                            {appointment.payment_status || 'N/A'}
                          </span>
                        </td> */}
                        <td>
                          <button 
                            className="view-btn"
                            onClick={() => openMedicineModal(appointment.medicine_details)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Modal for Medicine List */}
              {isModalOpen && selectedMedicines && (
                <div className="modal-overlay">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h3>Medicine List</h3>
                      <button className="modal-close-btn" onClick={closeMedicineModal}>
                        <X size={20} />
                      </button>
                    </div>
                    <div className="modal-body">
                      <table className="medicine-table">
                        <thead>
                          <tr>
                            <th>Medicine Name</th>
                            <th>Morning</th>
                            <th>Noon</th>
                            <th>Evening</th>
                            <th>Night</th>
                            <th>Food</th>
                            <th>Days</th>
                            <th>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedMedicines.map((medicine, index) => (
                            <tr key={index}>
                              <td>{medicine.name}</td>
                              <td>{medicine.counts[0]}</td>
                              <td>{medicine.counts[1]}</td>
                              <td>{medicine.counts[2]}</td>
                              <td>{medicine.counts[3]}</td>
                              <td>
                                <span className={`food-badge ${medicine.foodType === 0 ? 'before-food' : 'after-food'}`}>
                                  {getFoodType(medicine.foodType)}
                                </span>
                              </td>
                              <td>{medicine.days}</td>
                              <td>{formatCurrency(medicine.price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="modal-footer">
                        <p>Total Amount: {formatCurrency(selectedMedicines.reduce((total, med) => total + med.price, 0))}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    <span>Page {currentPage} of {totalPages}</span>
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn" 
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>
                    
                    <div className="pagination-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn" 
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PharmacyAppointments;